
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calculator, AlertTriangle, TrendingUp, Save } from "lucide-react";
import { PricingStandard } from "../../../types";

interface RateCalculatorProps {
  pricingStandards: PricingStandard[];
}

const RateCalculator = ({ pricingStandards }: RateCalculatorProps) => {
  const [formData, setFormData] = useState({
    itemName: "",
    category: "",
    unit: "",
    materialCost: 0,
    labourCost: 0,
    overhead: 0,
    baseProfit: 0,
    riskMultiplier: 1.0,
    region: ""
  });

  const [calculatedRate, setCalculatedRate] = useState({
    allInRate: 0,
    profitAfterRisk: 0,
    totalCost: 0,
    profitMargin: 0
  });

  const calculateRate = () => {
    const baseCost = formData.materialCost + formData.labourCost + formData.overhead;
    const riskAdjustedProfit = formData.baseProfit * formData.riskMultiplier;
    const allInRate = baseCost + riskAdjustedProfit;
    const profitMargin = (riskAdjustedProfit / allInRate) * 100;

    setCalculatedRate({
      allInRate,
      profitAfterRisk: riskAdjustedProfit,
      totalCost: baseCost,
      profitMargin
    });
  };

  const getRiskLevel = (multiplier: number) => {
    if (multiplier <= 1.1) return { level: "Low", color: "bg-green-500" };
    if (multiplier <= 1.3) return { level: "Medium", color: "bg-yellow-500" };
    if (multiplier <= 1.5) return { level: "High", color: "bg-orange-500" };
    return { level: "Critical", color: "bg-red-500" };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const riskLevel = getRiskLevel(formData.riskMultiplier);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Input Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calculator className="h-5 w-5 mr-2" />
            All-in-Rate Calculator
          </CardTitle>
          <CardDescription>
            Calculate comprehensive rates including material, labor, overhead, and risk-adjusted profit
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="itemName">Item Name</Label>
              <Input
                id="itemName"
                value={formData.itemName}
                onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                placeholder="Enter item name"
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="construction">Construction Materials</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="services">Services</SelectItem>
                  <SelectItem value="utilities">Utilities</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="unit">Unit of Measure</Label>
              <Input
                id="unit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="e.g., m³, kg, unit"
              />
            </div>
            <div>
              <Label htmlFor="region">Region</Label>
              <Select value={formData.region} onValueChange={(value) => setFormData({ ...formData, region: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lagos">Lagos</SelectItem>
                  <SelectItem value="abuja">Abuja</SelectItem>
                  <SelectItem value="kano">Kano</SelectItem>
                  <SelectItem value="port-harcourt">Port Harcourt</SelectItem>
                  <SelectItem value="ibadan">Ibadan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium">Cost Components (NGN)</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="materialCost">Material Cost</Label>
                <Input
                  id="materialCost"
                  type="number"
                  value={formData.materialCost}
                  onChange={(e) => setFormData({ ...formData, materialCost: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="labourCost">Labour Cost</Label>
                <Input
                  id="labourCost"
                  type="number"
                  value={formData.labourCost}
                  onChange={(e) => setFormData({ ...formData, labourCost: Number(e.target.value) })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="overhead">Overhead</Label>
                <Input
                  id="overhead"
                  type="number"
                  value={formData.overhead}
                  onChange={(e) => setFormData({ ...formData, overhead: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="baseProfit">Base Profit</Label>
                <Input
                  id="baseProfit"
                  type="number"
                  value={formData.baseProfit}
                  onChange={(e) => setFormData({ ...formData, baseProfit: Number(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="riskMultiplier">Risk Multiplier</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="riskMultiplier"
                  type="number"
                  step="0.1"
                  min="1.0"
                  max="3.0"
                  value={formData.riskMultiplier}
                  onChange={(e) => setFormData({ ...formData, riskMultiplier: Number(e.target.value) })}
                />
                <Badge className={`${riskLevel.color} text-white`}>
                  {riskLevel.level} Risk
                </Badge>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                1.0 = No Risk, 1.5 = High Risk, 2.0+ = Critical Risk
              </p>
            </div>
          </div>

          <Button onClick={calculateRate} className="w-full">
            <Calculator className="h-4 w-4 mr-2" />
            Calculate All-in-Rate
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Rate Breakdown
          </CardTitle>
          <CardDescription>
            Detailed cost analysis and pricing recommendations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {calculatedRate.allInRate > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-700">All-in-Rate</h4>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatCurrency(calculatedRate.allInRate)}
                  </p>
                  <p className="text-sm text-blue-600">Per {formData.unit || 'unit'}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-700">Profit Margin</h4>
                  <p className="text-2xl font-bold text-green-900">
                    {calculatedRate.profitMargin.toFixed(1)}%
                  </p>
                  <p className="text-sm text-green-600">After risk adjustment</p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium">Cost Breakdown</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span>Material Cost</span>
                    <span className="font-medium">{formatCurrency(formData.materialCost)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span>Labour Cost</span>
                    <span className="font-medium">{formatCurrency(formData.labourCost)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span>Overhead</span>
                    <span className="font-medium">{formatCurrency(formData.overhead)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span>Base Profit</span>
                    <span className="font-medium">{formatCurrency(formData.baseProfit)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span>Risk-Adjusted Profit</span>
                    <span className="font-medium text-yellow-700">
                      {formatCurrency(calculatedRate.profitAfterRisk)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-blue-50 rounded border-t-2 border-blue-200">
                    <span className="font-medium">Total All-in-Rate</span>
                    <span className="font-bold text-blue-700">
                      {formatCurrency(calculatedRate.allInRate)}
                    </span>
                  </div>
                </div>
              </div>

              {formData.riskMultiplier > 1.3 && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <h4 className="font-medium text-orange-700">High Risk Alert</h4>
                  </div>
                  <p className="text-sm text-orange-600 mt-1">
                    This project has elevated risk factors. Consider additional contingencies and detailed risk analysis.
                  </p>
                </div>
              )}

              <Button variant="outline" className="w-full">
                <Save className="h-4 w-4 mr-2" />
                Save as Standard Rate
              </Button>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calculator className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Enter cost components and click calculate to see the rate breakdown</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RateCalculator;

